import { Check, Copy, ExternalLink, Globe, Share2 } from 'lucide-react';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import GalleryLinkToggle from './GalleryLinkToggle';
import { shareableGalleryUrl, type GalleryLinkGate } from './shareGate';
import type { PublicLink } from './usePublicLink';

interface GalleryQuickMenuProps {
  link: PublicLink;
  gate: GalleryLinkGate;
  onAllOptions: () => void;
}

const rowClass =
  'flex w-full items-center gap-3 rounded-sm p-2 text-left text-foreground hover:bg-accent focus-visible:bg-accent focus-visible:outline-none';
const iconCircleClass =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground transition-colors';

/**
 * The header globe's popover (DV-3714, Header Actions 2e): the PlanOtour
 * switch and, while the link is on, Copy link and Open PlanOtour, plus a
 * way into the full Share modal. The switch is the same action as the Share
 * modal's tab, with the same toasts.
 */
const GalleryQuickMenu = ({ link, gate, onAllOptions }: GalleryQuickMenuProps) => {
  const liveUrl = shareableGalleryUrl(link.url, gate);
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 p-2">
        <span
          aria-hidden
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-info-soft text-info-soft-foreground"
        >
          <Globe className="h-4 w-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-semibold">PlanOtour</span>
          <span className="text-xs text-muted-foreground">Share these photos with a public link</span>
        </div>
      </div>
      <GalleryLinkToggle link={link} gate={gate} className="mx-1 mb-1" />
      {liveUrl !== null && (
        <>
          <div className="my-1 border-t border-border" />
          {/* Not while a revoke is out: it would copy a link about to die. */}
          <button
            type="button"
            onClick={link.copy}
            disabled={link.busy}
            className={cn(rowClass, 'disabled:pointer-events-none disabled:opacity-50')}
          >
            <span
              className={cn(
                iconCircleClass,
                link.copied && 'bg-success-soft text-success-soft-foreground'
              )}
            >
              {link.copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-sm font-medium">
                {link.copied ? 'Copied to clipboard' : 'Copy link'}
              </span>
              <span className="truncate font-mono text-xs text-muted-foreground">{liveUrl}</span>
            </span>
          </button>
          <a href={liveUrl} target="_blank" rel="noopener noreferrer" className={rowClass}>
            <span className={iconCircleClass}>
              <ExternalLink className="h-4 w-4" />
            </span>
            <span className="text-sm font-medium">Open PlanOtour</span>
          </a>
        </>
      )}
      <div className="my-1 border-t border-border" />
      <button type="button" onClick={onAllOptions} className={cn(rowClass, 'text-muted-foreground')}>
        <Share2 className="ml-[9px] h-3.5 w-3.5" aria-hidden />
        <Typography variant="body-sm" className="text-muted-foreground">
          All sharing options…
        </Typography>
      </button>
    </div>
  );
};

export default GalleryQuickMenu;
